import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from './components/Layout.js';
import { RouteTransition } from './components/RouteTransition.js';
import { SeitenFehler } from './components/SeitenFehler.js';
import { EmptyPanel, LoadingPanel } from './components/StatePanel.js';
import { LEGACY_ROUTES } from './lib/nav.js';
import { AuditPage } from './pages/Audit.js';
import { CatalogListPage } from './pages/CatalogList.js';
import { DerivationsPage } from './pages/Derivations.js';
import { OverviewPage } from './pages/Overview.js';

// ECharts-Vendor liegt in einem eigenen Chunk (~350 KB gzip). Die Pages, die
// ECharts direkt rendern, werden via dynamic import lazy geladen, damit
// Catalog/Audit/Derivations beim ersten Login ohne den Chart-Vendor laden.
const DashboardPage = lazy(() =>
  import('./pages/Dashboard.js').then((m) => ({ default: m.DashboardPage })),
);
const DatasetDetailPage = lazy(() =>
  import('./pages/DatasetDetail.js').then((m) => ({ default: m.DatasetDetailPage })),
);
const BuilderPage = lazy(() =>
  import('./pages/Builder.js').then((m) => ({ default: m.BuilderPage })),
);
const ExplorePage = lazy(() =>
  import('./pages/Explore.js').then((m) => ({ default: m.ExplorePage })),
);
const AlertsPage = lazy(() => import('./pages/Alerts.js').then((m) => ({ default: m.AlertsPage })));
const GalleryPage = lazy(() => import('./pages/Gallery.js').then((m) => ({ default: m.GalleryPage })));
const ForecastsPage = lazy(() =>
  import('./pages/Forecasts.js').then((m) => ({ default: m.ForecastsPage })),
);
// Digitaler Zwilling: MapLibre + Babylon liegen im eigenen Chunk (vite.config).
const TwinPage = lazy(() => import('./pages/Twin.js').then((m) => ({ default: m.TwinPage })));
const NewSourcePage = lazy(() =>
  import('./pages/NewSource.js').then((m) => ({ default: m.NewSourcePage })),
);
const SourcesPage = lazy(() =>
  import('./pages/Sources.js').then((m) => ({ default: m.SourcesPage })),
);
const SilosPage = lazy(() =>
  import('./pages/Silos.js').then((m) => ({ default: m.SilosPage })),
);
const AdminLlmConfigPage = lazy(() =>
  import('./pages/AdminLlmConfig.js').then((m) => ({ default: m.AdminLlmConfigPage })),
);
const AdminAccessPage = lazy(() =>
  import('./pages/AdminAccess.js').then((m) => ({ default: m.AdminAccessPage })),
);
const AdminLinkTilesPage = lazy(() =>
  import('./pages/AdminLinkTiles.js').then((m) => ({ default: m.AdminLinkTilesPage })),
);
const AdminSystemPage = lazy(() =>
  import('./pages/AdminSystem.js').then((m) => ({ default: m.AdminSystemPage })),
);
const AdminQualityPage = lazy(() =>
  import('./pages/AdminQuality.js').then((m) => ({ default: m.AdminQualityPage })),
);
const SqlLabPage = lazy(() => import('./pages/SqlLab.js').then((m) => ({ default: m.SqlLabPage })));
const SparqlLabPage = lazy(() =>
  import('./pages/SparqlLab.js').then((m) => ({ default: m.SparqlLabPage })),
);
const AdminHarvestPage = lazy(() =>
  import('./pages/AdminHarvest.js').then((m) => ({ default: m.AdminHarvestPage })),
);
const AdminPublishersPage = lazy(() =>
  import('./pages/AdminCatalogEntities.js').then((m) => ({ default: m.AdminPublishersPage })),
);
const AdminDataServicesPage = lazy(() =>
  import('./pages/AdminCatalogEntities.js').then((m) => ({ default: m.AdminDataServicesPage })),
);
const AdminMapServicesPage = lazy(() =>
  import('./pages/AdminMapServices.js').then((m) => ({ default: m.AdminMapServicesPage })),
);
const AdminTwinBasedataPage = lazy(() =>
  import('./pages/AdminTwinBasedata.js').then((m) => ({ default: m.AdminTwinBasedataPage })),
);
const AdminBenutzerPage = lazy(() =>
  import('./pages/AdminBenutzer.js').then((m) => ({ default: m.AdminBenutzerPage })),
);
const AdminGliederungPage = lazy(() =>
  import('./pages/AdminGliederung.js').then((m) => ({ default: m.AdminGliederungPage })),
);
const AdminProvidersPage = lazy(() =>
  import('./pages/AdminProviders.js').then((m) => ({ default: m.AdminProvidersPage })),
);

export default function App(): JSX.Element {
  const { pathname } = useLocation();
  return (
    <Layout>
      <SeitenFehler resetKey={pathname}>
      <Suspense fallback={<LoadingPanel label="Seite wird geladen..." />}>
        <RouteTransition>
        <Routes>
          {/* "/" liefert nginx als oeffentliche Startseite aus; in der App fuehrt es zur Uebersicht. */}
          <Route path="/" element={<Navigate to="/uebersicht" replace />} />
          <Route path="/uebersicht" element={<OverviewPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/:slug" element={<DashboardPage />} />
          <Route path="/catalog" element={<CatalogListPage />} />
          <Route path="/silos" element={<SilosPage />} />
          <Route path="/sources/new" element={<NewSourcePage />} />
          <Route path="/catalog/:id" element={<DatasetDetailPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/derivations" element={<DerivationsPage />} />
          <Route path="/forecasts" element={<ForecastsPage />} />
          <Route path="/twin" element={<TwinPage />} />
          <Route path="/builder" element={<BuilderPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          {/* Backwards-Compat: alte Bookmarks auf /widget-builder leiten auf /builder?intent=widget um. */}
          <Route path="/widget-builder" element={<WidgetBuilderRedirect />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          <Route path="/admin/llm-config" element={<AdminLlmConfigPage />} />
          <Route path="/admin/access" element={<AdminAccessPage />} />
          <Route path="/admin/link-tiles" element={<AdminLinkTilesPage />} />
          <Route path="/admin/system" element={<AdminSystemPage />} />
          <Route path="/admin/quality" element={<AdminQualityPage />} />
          <Route path="/admin/sql-lab" element={<SqlLabPage />} />
          <Route path="/admin/sparql" element={<SparqlLabPage />} />
          <Route path="/admin/harvest" element={<AdminHarvestPage />} />
          <Route path="/admin/publishers" element={<AdminPublishersPage />} />
          <Route path="/admin/data-services" element={<AdminDataServicesPage />} />
          {Object.entries(LEGACY_ROUTES).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/admin/maps" element={<AdminMapServicesPage />} />
          <Route path="/admin/twin-basedata" element={<AdminTwinBasedataPage />} />
          <Route path="/admin/gliederung" element={<AdminGliederungPage />} />
          <Route path="/admin/benutzer" element={<AdminBenutzerPage />} />
          <Route path="/admin/tenant" element={<Navigate to="/admin/gliederung" replace />} />
          <Route path="/admin/providers" element={<AdminProvidersPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </RouteTransition>
      </Suspense>
      </SeitenFehler>
    </Layout>
  );
}

function WidgetBuilderRedirect(): JSX.Element {
  return <Navigate to="/builder?intent=widget" replace />;
}

function NotFound(): JSX.Element {
  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-display font-semibold tracking-tight">Seite nicht gefunden</h1>
      <EmptyPanel>Diese Ansicht wird in einem spaeteren Epic implementiert.</EmptyPanel>
    </div>
  );
}
